import React, { useContext, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment';
import TimePicker from 'rc-time-picker';
import 'rc-time-picker/assets/index.css';

import AppContext from '../contexts/AppContext';
import ThemeContext from '../contexts/ThemeContext';
import FunctionsContext from '../contexts/FunctionsContext';

import GlobeIcon from '../images/GlobeIcon';
import SearchSelect from '../components/SearchSelect';
import Datepicker from '../components/Datepicker';
import ChipGroup from '../components/ChipGroup';
import Label from '../components/Label';
import {
  CardContainer,
  TwoColumnLayout,
  SectionTitle as SharedSectionTitle,
  FormContent,
  Divider as SharedDivider,
  StepContainer,
  Section,
  Input,
  InputWithIcon,
  Textarea,
  ErrorText,
  HelpText,
  Required,
  RoundedButton,
} from '../components/PrebookingSharedComponents';

import i18n from '../i18n';
import { getDurationTitle, getMinSelectableDate, getEarliestPrebookingDateTime, isBeyondMaxBookingDate } from '../utils/timeUtils';
import { getActiveContractsFromUser, getContractById } from '../utils/contractUtils';
import { getBrandPrebookingDurations, getPrebookingDurations } from '../utils/durationOptions';
import { CalendarIcon, TimeIcon, DeleteIcon, AddCircleIcon } from '../components/PrebookingIcons';
import { isHeroBrandEnabled } from '../utils/heroBrandUtils';


// Sargamicons SVG Icon Components (from Figma design)
const SmartphoneIcon = ({ color = '#45446b', size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 14 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 4H8M3.4 1H10.6C11.9255 1 13 2.07452 13 3.4V18.6C13 19.9255 11.9255 21 10.6 21H3.4C2.07452 21 1 19.9255 1 18.6V3.4C1 2.07452 2.07452 1 3.4 1Z" stroke={color} strokeOpacity="0.8" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BuildingIcon = ({ color = '#45446b', size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 4V18.5999C19 19.9254 17.9255 20.9999 16.6 20.9999H5.4C4.07452 20.9999 3 19.9254 3 18.5999V4M14 21V17.8C14 17.3582 13.6418 17 13.2 17H8.8C8.35817 17 8 17.3582 8 17.8V21M11 7V9M11 12V14M15 7V9M15 12V14M7 7V9M7 12V14M1.8 1H20.2C20.6418 1 21 1.35817 21 1.8V3.2C21 3.64183 20.6418 4 20.2 4H1.8C1.35817 4 1 3.64183 1 3.2V1.8C1 1.35817 1.35817 1 1.8 1Z" stroke={color} strokeOpacity="0.8" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const NoteIcon = ({ color = '#45446b', size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 20 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3C12 4.10457 11.1046 5 10 5C8.89543 5 8 4.10457 8 3M12 3C12 1.89543 11.1046 1 10 1C8.89543 1 8 1.89543 8 3M12 3H16.6C17.9255 3 19 4.07452 19 5.4V20.6C19 21.9255 17.9255 23 16.6 23H3.4C2.07452 23 1 21.9255 1 20.6V5.4C1 4.07452 2.07452 3 3.4 3H8M5 13H9M5 10H15M5 16H7M12.306 13.7761L11.9291 15.2836C11.9112 15.3552 11.8552 15.4112 11.7836 15.4291L10.2761 15.806C10.0741 15.8565 10.0741 16.1435 10.2761 16.194L11.7836 16.5709C11.8552 16.5888 11.9112 16.6448 11.9291 16.7164L12.306 18.2239C12.3565 18.4259 12.6435 18.4259 12.694 18.2239L13.0709 16.7164C13.0888 16.6448 13.1448 16.5888 13.2164 16.5709L14.7239 16.194C14.9259 16.1435 14.9259 15.8565 14.7239 15.806L13.2164 15.4291C13.1448 15.4112 13.0888 15.3552 13.0709 15.2836L12.694 13.7761C12.6435 13.5741 12.3565 13.5741 12.306 13.7761Z" stroke={color} strokeOpacity="0.8" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PhoneIcon = ({ color, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.7871 5.84615L8.12259 10.1539C8.58711 9.61539 11.529 6.46153 13 10.7692C13 10.7692 12.8452 13 9.67098 13C7.42582 13 5.10324 10.3077 3.47743 8.76922C2.08388 7.46153 1 5.76922 1 4.23076C1 1.07691 3.16773 1 3.16773 1C8.12257 2.69231 3.7871 5.84615 3.7871 5.84615Z" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinejoin="round"/>
  </svg>
);

const VideoIcon = ({ color, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 17 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.06667 1H2.6C1.71634 1 1 1.71634 1 2.6V7.4C1 8.28366 1.71634 9 2.6 9H8.06667C8.95032 9 9.66667 8.28366 9.66667 7.4V2.6C9.66667 1.71634 8.95032 1 8.06667 1Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M15.6667 1L11.6667 5L15.6667 9V1Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);

const MonitorIcon = ({ color, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.1 16.5H11.9M3.42 1.5H15.58C16.6404 1.5 17.5 2.50736 17.5 3.75V11.4375C17.5 12.6801 16.6404 13.6875 15.58 13.6875H3.42C2.35961 13.6875 1.5 12.6801 1.5 11.4375V3.75C1.5 2.50736 2.35961 1.5 3.42 1.5Z" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 13C14 10.7909 12.1868 10 9.95 10H9.05C6.81325 10 5 10.7909 5 13M9.5 8.5C8.39543 8.5 7.5 7.60457 7.5 6.5V6C7.5 4.89543 8.39543 4 9.5 4C10.6046 4 11.5 4.89543 11.5 6V6.5C11.5 7.60457 10.6046 8.5 9.5 8.5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TwoColumnSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h3`
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 1.4;
  color: ${({ colors }) => colors?.twilight || '#45446b'};
  margin: 0 0 10px 0;
`;

// Tab-style presence buttons (Figma design)
const PresenceTabGroup = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const PresenceTab = styled.button`
  flex: 1;
  padding: 4px 0;
  border: none;
  border-bottom: ${({ selected }) =>
    selected ? '2px solid #45446b' : '1px solid rgba(69, 68, 107, 0.2)'};
  background: none;
  color: #45446b;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: ${({ selected }) => (selected ? '600' : '500')};
  letter-spacing: 0.5px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.2s;
  border-top: none;
  border-left: none;
  border-right: none;
  padding-bottom: ${({ selected }) => (selected ? '2px' : '3px')};

  &:hover {
    background-color: rgba(69, 68, 107, 0.05);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const InfoText = styled.p`
  font-size: 14px;
  color: ${({ colors }) => colors?.twilight || '#45446b'};
  margin: 0;
  padding: 8px 0;
`;

const ExplanationBox = styled.div`
  background-color: #F5F7FA;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;

  strong {
    font-weight: 600;
    color: ${({ colors }) => colors?.twilight || '#45446b'};
  }

  p {
    margin: 8px 0 0 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ colors }) => colors?.gray || '#666'};
  }
`;

const InputGroup = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;

  label {
    margin-bottom: 8px;
    position: relative;
    z-index: 1;
  }

  input {
    margin-top: 0;
  }
`;


// Global styles for TimePicker and DatePicker - only applied when this component is mounted
const TimePickerGlobalStyle = createGlobalStyle`
  .prebooking-time-slot .rc-time-picker {
    width: 100%;
    height: 40px;
  }

  .prebooking-time-slot .rc-time-picker-input {
    width: 100%;
    height: 40px;
    padding: 8px;
    padding-left: 40px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.5px;
    line-height: 1.4;
    background-color: #f6f8fb;
    color: #333333;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: #45446b;
      color: #45446b;
    }
  }

  .prebooking-time-slot .rc-time-picker-panel-select {
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
  }
`;

// Time Slot Components - Two Row Layout
const TimeSlotContainer = styled.div`
  margin-bottom: 16px;
`;

const TimeSlotLabelsRow = styled.div`
  display: grid;
  grid-template-columns: 234px 1fr 1fr 120px 40px;
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 6px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const TimeSlotInputsRow = styled.div`
  display: grid;
  grid-template-columns: 234px 1fr 1fr 120px 40px;
  gap: 16px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const TimeSlotErrorRow = styled.div`
  display: grid;
  grid-template-columns: 234px 1fr 1fr 120px 40px;
  gap: 16px;
  margin-top: 4px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

const RemoveSlotButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: rgba(69, 68, 107, 1);
  cursor: pointer;
  transition: all 0.2s;
  padding: 8px 0;

  &:hover {
    color: #EA7E8F;

    svg path {
      stroke: #EA7E8F;
    }
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const TimeSlotDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: rgba(69, 68, 107, 0.2);
  margin: 16px 0;
`;

const AddSlotButton = styled(RoundedButton)`
  margin-top: 8px;
`;

// Textarea with larger min-height for message content
const MessageTextarea = styled(Textarea)`
  min-height: 150px;
`;

const EndTimeDisplay = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  padding: 8px 0;
  color: rgba(69, 68, 107, 0.6);
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  line-height: 1.4;
`;

const AddButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
  gap: 10px;
`;

const PrebookingDetailsStep = forwardRef(({ formData, updateField, addTimeSlot, removeTimeSlot, updateTimeSlot, wizardMode }, ref) => {
  const { user } = useContext(AppContext);
  const { colors } = useContext(ThemeContext);
  const { fetchLanguageList } = useContext(FunctionsContext);

  const [errors, setErrors] = useState({});
  const activeContracts = React.useMemo(() => getActiveContractsFromUser(user), [user]);
  const selectedContract = React.useMemo(() => {
    const fromSelection = getContractById(activeContracts, formData.selectedContractId);
    return fromSelection?.raw || activeContracts[0]?.raw || null;
  }, [activeContracts, formData.selectedContractId]);

  // Determine which presence tabs are available based on contract interpretation types
  const availablePresenceTypes = React.useMemo(() => {
    const types = selectedContract?.interpretationTypes;
    if (!types || types.length === 0) return { remote: true, onsite: true, notice: true };

    const hasRemote = types.includes('prebooking_remote');
    const hasExternal = types.includes('prebooking_external');
    return {
      remote: hasRemote || hasExternal,
      onsite: types.includes('prebooking_onsite'),
      notice: types.includes('prebooking_notice'),
    };
  }, [selectedContract?.interpretationTypes]);

  // Auto-select first available presence if current is hidden
  useEffect(() => {
    if (!availablePresenceTypes[formData.presence]) {
      const first = ['remote', 'onsite', 'notice'].find(p => availablePresenceTypes[p]);
      if (first) updateField('presence', first);
    }
  }, [availablePresenceTypes, formData.presence, updateField]);

  // User group settings for time validation and granularity
  const prebookingStartingTimeMinutes = user.userGroup?.prebookingStartingTimeMinutes || 15;
  const MAX_MINUTES_STEP = 60;

  // Compute earliest allowed booking datetime based on presence-specific bumpers.
  // Usergroup-then-contract resolution, inverse of the duration ladder (TULQA-585).
  const earliestDateTime = React.useMemo(() =>
    getEarliestPrebookingDateTime(
      formData.presence,
      user.userGroup,
      selectedContract,
      prebookingStartingTimeMinutes
    ),
    [formData.presence, user.userGroup, selectedContract, prebookingStartingTimeMinutes]
  );

  const minSelectableDate = React.useMemo(() => {
    if (earliestDateTime) {
      return earliestDateTime.clone().startOf('day').toDate();
    }
    return getMinSelectableDate(formData.presence); // No bumper configured
  }, [earliestDateTime, formData.presence]);

  // Helper functions for time validation
  const getDisabledHours = (date) => {
    if (!earliestDateTime) return [];
    const earliestDate = earliestDateTime.clone().startOf('day');
    if (!moment(date).isSame(earliestDate, 'day')) return [];
    return [...Array(earliestDateTime.hour()).keys()];
  };

  const getDisabledMinutes = (date, selectedHour) => {
    if (!earliestDateTime) return [];
    const earliestDate = earliestDateTime.clone().startOf('day');
    if (!moment(date).isSame(earliestDate, 'day')) return [];
    if (selectedHour === earliestDateTime.hour()) {
      return [...Array(earliestDateTime.minute()).keys()];
    }
    return [];
  };

  // Handler functions for date and time changes with auto-adjustment
  const handleDateChange = (index, date) => {
    const slot = formData.timeSlots[index];
    updateTimeSlot(index, 'date', date);

    // Clear date error when user selects a date
    if (date && errors[`timeSlot_${index}_date`]) {
      const newErrors = { ...errors };
      delete newErrors[`timeSlot_${index}_date`];
      setErrors(newErrors);
    }

    // Auto-adjust start time if it's now invalid due to date change
    if (slot.startTime) {
      let selectedDateTime = moment(date);
      selectedDateTime.set({
        hour: moment(slot.startTime).hour(),
        minute: moment(slot.startTime).minute(),
        second: 0,
        millisecond: 0
      });

      // Auto-adjust if before earliest allowed time
      if (earliestDateTime && selectedDateTime.isBefore(earliestDateTime)) {
        selectedDateTime = earliestDateTime.clone();
      } else {
        selectedDateTime = moment(slot.startTime); // No adjustment needed
      }

      // Only update if time actually changed
      if (!moment(slot.startTime).isSame(selectedDateTime)) {
        updateTimeSlot(index, 'startTime', selectedDateTime.toDate());

        // Recalculate end time if duration exists
        if (slot.duration) {
          const endTime = new Date(selectedDateTime.toDate().getTime() + slot.duration * 60000);
          updateTimeSlot(index, 'endTime', endTime);
        }
      }
    }
  };

  const handleTimeChange = (index, time) => {
    if (time) {
      const slot = formData.timeSlots[index];

      // Build the selected datetime
      let selectedDateTime = moment(slot.date);
      selectedDateTime.set({
        hour: moment(time).hour(),
        minute: moment(time).minute(),
        second: 0,
        millisecond: 0
      });

      // Auto-adjust if before earliest allowed time
      if (earliestDateTime && selectedDateTime.isBefore(earliestDateTime)) {
        selectedDateTime = earliestDateTime.clone();
      }

      updateTimeSlot(index, 'startTime', selectedDateTime.toDate());

      // Clear time error when user selects a time
      if (errors[`timeSlot_${index}_time`]) {
        const newErrors = { ...errors };
        delete newErrors[`timeSlot_${index}_time`];
        setErrors(newErrors);
      }

      // Recalculate end time if duration exists
      if (slot.duration) {
        const endTime = new Date(selectedDateTime.toDate().getTime() + slot.duration * 60000);
        updateTimeSlot(index, 'endTime', endTime);
      }
    } else {
      updateTimeSlot(index, 'startTime', null);
    }
  };

  // Duration options in minutes, filtered by Contract/UG min/max duration settings.
  // Contract values take precedence over user group (pricing model) values; user group is
  // used only as a fallback when the contract does not define the limit (TULQA-585).
  // Fallback order for on-site is: contract on-site -> contract general -> userGroup
  // on-site -> userGroup general. Semantics for an explicit 0 differ by end:
  //   - min: 0 means "no minimum required" -> minDuration becomes 0.
  //   - max: 0 means "no upper limit"       -> fall through to the next candidate /
  //     default rather than capping at 0 minutes.
  const isHero = isHeroBrandEnabled();
  const slotDurationsKey = isHero ? (formData.timeSlots || []).map(slot => slot.duration).join(',') : '';
  const durationOptions = React.useMemo(() => {
    let minDuration = 15;
    let maxDuration = 480;

    const firstDefined = (...values) => values.find(v => v !== null && v !== undefined);
    const firstPositive = (...values) => values.find(v => typeof v === 'number' && v > 0);

    if (formData.presence === 'onsite') {
      // Backend values are in seconds, convert to minutes for comparison
      const minSeconds = firstDefined(
        selectedContract?.minPbOnSiteDuration,
        selectedContract?.minPbDuration,
        user?.userGroup?.minOnSiteDuration,
        user?.userGroup?.minPrebookingDuration,
      );
      const maxSeconds = firstPositive(
        selectedContract?.maxPbOnSiteDuration,
        selectedContract?.maxPbDuration,
        user?.userGroup?.maxOnSiteDuration,
        user?.userGroup?.maxPrebookingDuration,
      );
      if (minSeconds !== undefined) minDuration = minSeconds / 60;
      if (maxSeconds !== undefined) maxDuration = maxSeconds / 60;
    } else if (formData.presence === 'remote') {
      const minSeconds = selectedContract?.minPbDuration ?? user?.userGroup?.minPrebookingDuration;
      const maxSeconds = firstPositive(selectedContract?.maxPbDuration, user?.userGroup?.maxPrebookingDuration);
      if (minSeconds !== null && minSeconds !== undefined) minDuration = minSeconds / 60;
      if (maxSeconds !== undefined) maxDuration = maxSeconds / 60;
    }

    const currentMinutes = slotDurationsKey ? slotDurationsKey.split(',').map(Number) : [];
    return getPrebookingDurations({
      durations: getBrandPrebookingDurations(isHero),
      minMinutes: minDuration,
      maxMinutes: maxDuration,
      currentMinutes,
    })
      .map(minutes => ({
        value: minutes,
        label: getDurationTitle(minutes),
      }));
  }, [formData.presence, user?.userGroup, selectedContract, isHero, slotDurationsKey]);

  // Reset time slot dates/durations that become invalid when presence changes (TUL-4793)
  useEffect(() => {
    if (formData.presence === 'notice') return;
    if (!formData.timeSlots || formData.timeSlots.length === 0) return;

    const minDateMoment = moment(minSelectableDate).startOf('day');
    let hasChanges = false;

    const newTimeSlots = formData.timeSlots.map((slot) => {
      let updatedSlot = { ...slot };

      if (slot.date && moment(slot.date).isBefore(minDateMoment, 'day')) {
        updatedSlot = { ...updatedSlot, date: null, startTime: null, endTime: null };
        hasChanges = true;
      }

      if (slot.duration && !durationOptions.some(opt => opt.value === slot.duration)) {
        updatedSlot = { ...updatedSlot, duration: null, endTime: null };
        hasChanges = true;
      }

      return updatedSlot;
    });

    if (hasChanges) {
      updateField('timeSlots', newTimeSlots);
    }
  }, [formData.presence]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Fetch languages if not available in formData (fallback to API)
    if (!formData.selectableLanguages || formData.selectableLanguages.length === 0) {
      if (user.selectableLanguages && user.selectableLanguages.length > 0) {
        // Update formData with user context languages
        updateField('selectableLanguages', user.selectableLanguages);
      } else {
        // Fallback: fetch from API if user context is also empty
        fetchLanguageList().catch(err => {
          console.error('Failed to fetch languages:', err);
        });
      }
    }
  }, [formData.selectableLanguages, user.selectableLanguages, fetchLanguageList, updateField]);

  // Mode options for ChipGroup, filtered by contract interpretation types
  const modeOptions = React.useMemo(() => {
    const types = selectedContract?.interpretationTypes;
    const hasContract = types && types.length > 0;
    const hasExternalLevels = !hasContract || types.includes('prebooking_external');
    const hasRemoteLevels = !hasContract || types.includes('prebooking_remote');

    const options = [];

    if (hasExternalLevels) {
      options.push({ value: 'call', label: i18n('phone_call'), icon: <PhoneIcon color={colors.iconAccentColor} size={16} /> });
    }

    if (hasRemoteLevels && !isHeroBrandEnabled()) {
      options.push({ value: 'data', label: i18n('video_call'), icon: <VideoIcon color={colors.iconAccentColor} size={16} /> });
    }

    if (hasExternalLevels) {
      options.push({ value: 'conference', label: i18n('conference_call'), icon: <MonitorIcon color={colors.iconAccentColor} size={16} /> });
    }

    return options;
  }, [colors.iconAccentColor, selectedContract?.interpretationTypes]);

  // Auto-select first available mode if current mode is hidden
  useEffect(() => {
    if (formData.presence === 'remote' && modeOptions.length > 0) {
      const currentModeAvailable = modeOptions.some(opt => opt.value === formData.mode);
      if (!currentModeAvailable) {
        updateField('mode', modeOptions[0].value);
      }
    }
  }, [formData.presence, formData.mode, modeOptions, updateField]);

  const validateStep = () => {
    const newErrors = {};

    // Validate language
    if (!formData.selectedLanguageId) {
      newErrors.language = i18n('language_required');
    }

    // Validate that interpreted and target languages are different
    // Note: targetLanguages items have both 'id' (of the target_language row) and 'languageId' (actual language ID)
    // so we need to compare using languageId from targetLanguages
    if (formData.presence !== 'notice' && formData.selectedLanguageId && formData.selectedTargetLanguageId) {
      const selectedTargetLang = formData.targetLanguages?.find(l => l.id === formData.selectedTargetLanguageId);
      
      // Compare the actual language identifiers
      const targetLangId = selectedTargetLang?.languageId;
      
      if (formData.selectedLanguageId && targetLangId && formData.selectedLanguageId === targetLangId) {
        newErrors.targetLanguage = i18n('interpreted_and_target_language_must_differ', 'Interpreted language and target language must be different');
      }
    }

    // Validate presence-specific fields
    if (formData.presence === 'onsite') {
      if (!formData.streetAddress) {
        newErrors.streetAddress = i18n('required');
      }
      if (!formData.postCode) {
        newErrors.postCode = i18n('required');
      }
      if (!formData.city) {
        newErrors.city = i18n('required');
      }
      if (!formData.arrivalInstruction) {
        newErrors.arrivalInstruction = i18n('required');
      }
    }

    if (formData.presence === 'notice') {
      // For announcements, only validate message content and delivery date
      if (!formData.messageContent) {
        newErrors.messageContent = i18n('required');
      }
      if (!formData.lastDeliveryDate) {
        newErrors.lastDeliveryDate = i18n('required');
      } else if (isBeyondMaxBookingDate(formData.lastDeliveryDate)) {
        // TUL-5698: this becomes the announcement's interpretation_date
        newErrors.lastDeliveryDate = i18n('date_too_far_in_future');
      }
    } else {
      // Validate time slots for remote and onsite
      if (!formData.timeSlots || formData.timeSlots.length === 0) {
        newErrors.timeSlots = i18n('at_least_one_time_slot_required');
      } else {
        formData.timeSlots.forEach((slot, index) => {
          if (!slot.date) {
            newErrors[`timeSlot_${index}_date`] = i18n('date_required');
          } else {
            // Validate date is not before minimum allowed date for this presence type (TUL-4793)
            if (moment(slot.date).isBefore(moment(minSelectableDate), 'day')) {
              newErrors[`timeSlot_${index}_date`] = i18n('date_required');
            } else if (isBeyondMaxBookingDate(slot.date)) {
              // TUL-5698: a separator-less `250926` is parsed as the year 250926
              newErrors[`timeSlot_${index}_date`] = i18n('date_too_far_in_future');
            }
          }
          if (!slot.startTime) {
            newErrors[`timeSlot_${index}_time`] = i18n('time_required');
          } else {
            // Validate against earliest allowed datetime (if bumper applies)
            const slotDateTime = moment(slot.date);
            slotDateTime.set({
              hour: moment(slot.startTime).hour(),
              minute: moment(slot.startTime).minute(),
              second: 0,
              millisecond: 0
            });

            if (earliestDateTime && slotDateTime.isBefore(earliestDateTime)) {
              const errorKey = formData.presence === 'onsite'
                ? 'minimalPrebookingOnSiteTimeBumperError'
                : 'minimalPrebookingTimeBumperError';
              newErrors[`timeSlot_${index}_time`] = i18n(errorKey);
            }
          }
          if (!slot.duration) {
            newErrors[`timeSlot_${index}_duration`] = i18n('duration_required');
          }
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Expose validation method to parent via ref
  useImperativeHandle(ref, () => ({
    validate: validateStep
  }));

  const handleLanguageChange = (selectedOption) => {
    updateField('selectedLanguageId', selectedOption?.value || null);
    updateField('selectedDialect', null);

    // Clear language error when user selects a language
    if (selectedOption?.value && errors.language) {
      const newErrors = { ...errors };
      delete newErrors.language;
      setErrors(newErrors);
    }
  };

  const handleDialectChange = (selectedOption) => {
    updateField('selectedDialect', selectedOption?.value || null);
  };

  const selectedLanguage = formData.selectableLanguages?.find(lang => lang.id === formData.selectedLanguageId);

  // Get dialect skills from formData for selected language
  const dialectSkills = formData.selectedLanguageId && formData.selectableSkills && formData.selectableSkills[formData.selectedLanguageId]
    ? formData.selectableSkills[formData.selectedLanguageId].filter(skill => skill.category?.tag === 'dialect')
    : [];

  // Dialect options - only show if language has dialects
  const dialectOptions = dialectSkills.length > 0
    ? [
        { value: null, label: i18n('does_not_matter') },
        ...dialectSkills.map(skill => ({
          value: skill.id,
          label: skill.name,
        })),
      ]
    : [];

  return (
    <StepContainer colors={colors}>
      <CardContainer>
        <TwoColumnLayout>
          <SharedSectionTitle colors={colors}>{i18n('details')}</SharedSectionTitle>
          <FormContent>
            {/* Presence Selection - Tab Style */}
            <PresenceTabGroup colors={colors}>
              {availablePresenceTypes.remote && (
                <PresenceTab
                  selected={formData.presence === 'remote'}
                  onClick={() => updateField('presence', 'remote')}
                >
                  <SmartphoneIcon size={24} />
                  <span>{i18n('presence_remote')}</span>
                </PresenceTab>
              )}
              {availablePresenceTypes.onsite && (
                <PresenceTab
                  selected={formData.presence === 'onsite'}
                  onClick={() => updateField('presence', 'onsite')}
                >
                  <BuildingIcon size={24} />
                  <span>{i18n('presence_on_site')}</span>
                </PresenceTab>
              )}
              {availablePresenceTypes.notice && (
                <PresenceTab
                  selected={formData.presence === 'notice'}
                  onClick={() => updateField('presence', 'notice')}
                >
                  <NoteIcon size={24} />
                  <span>{i18n('presence_notice')}</span>
                </PresenceTab>
              )}
            </PresenceTabGroup>

            {/* REMOTE VIEW */}
            {formData.presence === 'remote' && (
              <>
                {/* Mode Selection */}
                <Section>
                  <Label text={i18n('mode_of_interpretation')} />
                  <ChipGroup
                    options={modeOptions}
                    selectedValues={formData.mode}
                    onChange={(value) => updateField('mode', value)}
                  />
                  {formData.mode === 'call' && (
                    <InfoText colors={colors}>
                      {i18n('phone_call_tip')}
                    </InfoText>
                  )}
                  {formData.mode === 'data' && (
                    <InfoText colors={colors}>
                      {i18n('video_call_tip')}
                    </InfoText>
                  )}
                  {formData.mode === 'conference' && (
                    <InfoText colors={colors}>
                      {i18n('conference_call_tip')}
                    </InfoText>
                  )}
                </Section>
                <SharedDivider />
              </>
            )}

            {/* ANNOUNCEMENT VIEW */}
            {formData.presence === 'notice' && (
              <ExplanationBox colors={colors}>
                <p>
                  <strong>{i18n('content_data_tooltip').split('.')[0]}.</strong>{' '}
                  {i18n('content_data_second_tooltip')}
                </p>
              </ExplanationBox>
            )}

            {/* Language Selection - All views */}
            <Section>
              <Label text={i18n('interpreted_language', 'Interpreted language')} required />
              <SearchSelect
                selected={selectedLanguage ? { value: selectedLanguage.id, label: selectedLanguage.name } : null}
                onChange={handleLanguageChange}
                selectableItems={(formData.selectableLanguages || []).map(lang => ({ value: lang.id, label: lang.name }))}
                placeholder={i18n('select')}
                customHeight="40px"
                customBackgroundColor="#f6f8fb"
                customBorderColor="rgba(0, 0, 0, 0.1)"
                icon={<GlobeIcon color={colors.iconAccentColor} size={20} />}
              />
              {errors.language && <ErrorText colors={colors}>{errors.language}</ErrorText>}
            </Section>

            {/* Dialect Selection - Show for all presence types if language has dialects.
                Rendered before "language you understand" (targetLanguage) so dialect is
                specified right after the main interpreted-language selection. */}
            {dialectOptions.length > 0 && (
              <>
                <SharedDivider />

                <Section>
                  <Label text={i18n('select_dialect')} />
                  <SearchSelect
                    selected={
                      formData.selectedDialect
                        ? dialectOptions.find(d => d.value === formData.selectedDialect)
                        : dialectOptions[0]
                    }
                    onChange={handleDialectChange}
                    selectableItems={dialectOptions}
                    placeholder={i18n('select')}
                    customHeight="40px"
                    customBackgroundColor="#f6f8fb"
                    customBorderColor="rgba(0, 0, 0, 0.1)"
                  />
                </Section>
              </>
            )}

            {/* Target Language Selection - Show when user has multiple target languages */}
            {formData.targetLanguages?.length > 1 && (
              <>
                <SharedDivider />
                <Section>
                  <Label text={i18n('targetLanguage')} />
                  <SearchSelect
                    selected={
                      formData.selectedTargetLanguageId
                        ? {
                            value: formData.selectedTargetLanguageId,
                            label: formData.targetLanguages.find(l => l.id === formData.selectedTargetLanguageId)?.name || ''
                          }
                        : formData.targetLanguages.length > 0
                          ? { value: formData.targetLanguages[0].id, label: formData.targetLanguages[0].name }
                          : null
                    }
                    onChange={(option) => {
                      updateField('selectedTargetLanguageId', option?.value || null);
                      // Clear target language error only when user selects a different language from interpreted language
                      if (option?.value && errors.targetLanguage && option.value !== formData.selectedLanguageId) {
                        const newErrors = { ...errors };
                        delete newErrors.targetLanguage;
                        setErrors(newErrors);
                      }
                    }}
                    selectableItems={formData.targetLanguages.map(lang => ({ value: lang.id, label: lang.name }))}
                    placeholder={i18n('select')}
                    customHeight="40px"
                    customBackgroundColor="#f6f8fb"
                    customBorderColor="rgba(0, 0, 0, 0.1)"
                    icon={<GlobeIcon color={colors.iconAccentColor} size={20} />}
                  />
                  {errors.targetLanguage && <ErrorText colors={colors}>{errors.targetLanguage}</ErrorText>}
                </Section>
              </>
            )}

            <SharedDivider />

            {/* TIME SLOTS - Only show for remote and onsite, NOT for announcements */}
            {formData.presence !== 'notice' && (
              <>
              <Section>
                <TimePickerGlobalStyle />
                <SectionTitle colors={colors}>
                  {i18n('date_and_time')}
                  {' '}
                  <Required colors={colors}>*</Required>
                </SectionTitle>

                {formData.timeSlots && formData.timeSlots.map((slot, index) => (
                  <React.Fragment key={index}>
                    <TimeSlotContainer>
                      {/* Labels Row */}
                      <TimeSlotLabelsRow>
                        <Label text={i18n('date')} required />
                        <Label text={i18n('start_time')} required />
                        <Label text={i18n('duration')} required />
                        <Label text={i18n('end_time')} />
                        <div />
                      </TimeSlotLabelsRow>

                      {/* Inputs Row */}
                      <TimeSlotInputsRow>
                        {/* Date Picker */}
                        <InputWithIcon hasIcon>
                          <span className="input-icon"><CalendarIcon color={colors.iconAccentColor} size={20} /></span>
                          <Datepicker
                            selected={slot.date}
                            onChange={(date) => handleDateChange(index, date)}
                            minDate={minSelectableDate}
                            placeholderText="DD.MM.20YY"
                            dateFormat="dd.M.yyyy"
                            customPadding="8px 8px 8px 40px"
                            customHeight="40px"
                            customBorder="1px solid rgba(0, 0, 0, 0.1)"
                          />
                        </InputWithIcon>

                        {/* Start Time Picker */}
                        <InputWithIcon hasIcon className="prebooking-time-slot">
                          <span className="input-icon"><TimeIcon color={colors.iconAccentColor} size={20} /></span>
                          <TimePicker
                            value={slot.startTime ? moment(slot.startTime) : null}
                            onChange={(time) => handleTimeChange(index, time ? time.toDate() : null)}
                            showSecond={false}
                            minuteStep={prebookingStartingTimeMinutes}
                            showMinute={prebookingStartingTimeMinutes < MAX_MINUTES_STEP}
                            format="HH:mm"
                            allowEmpty={false}
                            use12Hours={false}
                            disabledHours={() => getDisabledHours(slot.date)}
                            disabledMinutes={(hour) => getDisabledMinutes(slot.date, hour)}
                            hideDisabledOptions={true}
                            disabled={!slot.date}
                            inputReadOnly
                          />
                        </InputWithIcon>

                        {/* Duration Dropdown */}
                        <SearchSelect
                          selected={slot.duration ? durationOptions.find(d => d.value === slot.duration) : null}
                          onChange={(option) => {
                            updateTimeSlot(index, 'duration', option?.value || null);
                            // Clear duration error when user selects a duration
                            if (option?.value && errors[`timeSlot_${index}_duration`]) {
                              const newErrors = { ...errors };
                              delete newErrors[`timeSlot_${index}_duration`];
                              setErrors(newErrors);
                            }
                          }}
                          selectableItems={durationOptions}
                          placeholder={i18n('select')}
                          customHeight="40px"
                          customBackgroundColor="#f6f8fb"
                          customBorderColor="rgba(0, 0, 0, 0.1)"
                        />

                        {/* End Time Display */}
                        <EndTimeDisplay>
                          {slot.endTime ? moment(slot.endTime).format('HH:mm') : '-'}
                        </EndTimeDisplay>

                        {/* Remove Button - Only show if more than 1 slot */}
                        {formData.timeSlots.length > 1 ? (
                          <RemoveSlotButton
                            type="button"
                            onClick={() => removeTimeSlot(index)}
                            title={i18n('remove')}
                          >
                            <DeleteIcon color="#45446b" size={20} />
                          </RemoveSlotButton>
                        ) : (
                          <div />
                        )}
                      </TimeSlotInputsRow>

                      {/* Errors Row */}
                      {(errors[`timeSlot_${index}_date`] ||
                        errors[`timeSlot_${index}_time`] ||
                        errors[`timeSlot_${index}_duration`]) && (
                        <TimeSlotErrorRow>
                          <div>
                            {errors[`timeSlot_${index}_date`] && (
                              <ErrorText colors={colors}>{errors[`timeSlot_${index}_date`]}</ErrorText>
                            )}
                          </div>
                          <div>
                            {errors[`timeSlot_${index}_time`] && (
                              <ErrorText colors={colors}>{errors[`timeSlot_${index}_time`]}</ErrorText>
                            )}
                          </div>
                          <div>
                            {errors[`timeSlot_${index}_duration`] && (
                              <ErrorText colors={colors}>{errors[`timeSlot_${index}_duration`]}</ErrorText>
                            )}
                          </div>
                          <div />
                          <div />
                        </TimeSlotErrorRow>
                      )}
                    </TimeSlotContainer>

                    {/* Divider - Only show between slots, not after the last one */}
                    {index < formData.timeSlots.length - 1 && <TimeSlotDivider />}
                  </React.Fragment>
                ))}

                {/* Add Another Button - Hidden in edit mode since user should only modify existing slot */}
                {wizardMode !== 'edit' && (
                  <AddButtonWrapper>
                    <AddSlotButton type="button" onClick={addTimeSlot}>
                      <AddCircleIcon color={colors.iconAccentColor} size={20} />
                      {i18n('add_another')}
                    </AddSlotButton>
                  </AddButtonWrapper>
                )}

                {errors.timeSlots && (
                  <ErrorText colors={colors}>{errors.timeSlots}</ErrorText>
                )}
              </Section>

              <SharedDivider />
              </>
            )}

            {/* ANNOUNCEMENT - Message Content */}
            {formData.presence === 'notice' && (
              <>
                <Section>
                  <Label text={i18n('write_the_content')} required />
                  <MessageTextarea
                    colors={colors}
                    value={formData.messageContent || ''}
                    onChange={(e) => {
                      updateField('messageContent', e.target.value);
                      // Clear error when user types content
                      if (e.target.value && errors.messageContent) {
                        const newErrors = { ...errors };
                        delete newErrors.messageContent;
                        setErrors(newErrors);
                      }
                    }}
                    placeholder={i18n('select_notice')}
                    maxLength={1000}
                  />
                  <HelpText colors={colors}>
                    {i18n('content_data_tooltip')}
                  </HelpText>
                  {errors.messageContent && <ErrorText colors={colors}>{errors.messageContent}</ErrorText>}
                </Section>

                <Section>
                  <Label text={i18n('date')} />
                  <InputWithIcon colors={colors} hasIcon>
                    <span className="input-icon"><CalendarIcon color={colors.iconAccentColor} size={20} /></span>
                    <Datepicker
                      selected={formData.lastDeliveryDate}
                      onChange={(date) => {
                        updateField('lastDeliveryDate', date);
                        // Clear error when user selects a date
                        if (date && errors.lastDeliveryDate) {
                          const newErrors = { ...errors };
                          delete newErrors.lastDeliveryDate;
                          setErrors(newErrors);
                        }
                      }}
                      minDate={minSelectableDate}
                      placeholderText="DD.M.20YY"
                      dateFormat="dd.M.yyyy"
                      customPadding="8px 8px 8px 40px"
                      customHeight="40px"
                      customBorder="1px solid rgba(0, 0, 0, 0.1)"
                    />
                  </InputWithIcon>
                  <HelpText colors={colors}>
                    {i18n('calendar_tooltip_notice_interpretation')}
                  </HelpText>
                  {errors.lastDeliveryDate && <ErrorText colors={colors}>{errors.lastDeliveryDate}</ErrorText>}
                </Section>
              </>
            )}

            {/* ON-SITE - Address Fields */}
            {formData.presence === 'onsite' && (
              <>
                <Section>
                  <Label text={i18n('location_title_address')} required />
                  <Input
                    colors={colors}
                    type="text"
                    value={formData.streetAddress || ''}
                    onChange={(e) => {
                      updateField('streetAddress', e.target.value);
                      // Clear error when user types
                      if (e.target.value && errors.streetAddress) {
                        const newErrors = { ...errors };
                        delete newErrors.streetAddress;
                        setErrors(newErrors);
                      }
                    }}
                    placeholder={i18n('location_placeholder_address')}
                  />
                  {errors.streetAddress && <ErrorText colors={colors}>{errors.streetAddress}</ErrorText>}
                </Section>

                <TwoColumnSection>
                  <InputGroup>
                    <Label text={i18n('post_code')} required />
                    <Input
                      colors={colors}
                      type="text"
                      value={formData.postCode || ''}
                      onChange={(e) => {
                        updateField('postCode', e.target.value);
                        // Clear error when user types
                        if (e.target.value && errors.postCode) {
                          const newErrors = { ...errors };
                          delete newErrors.postCode;
                          setErrors(newErrors);
                        }
                      }}
                      placeholder={i18n('location_placeholder_postcode')}
                    />
                    {errors.postCode && <ErrorText colors={colors}>{errors.postCode}</ErrorText>}
                  </InputGroup>

                  <InputGroup>
                    <Label text={i18n('city')} required />
                    <Input
                      colors={colors}
                      type="text"
                      value={formData.city || ''}
                      onChange={(e) => {
                        updateField('city', e.target.value);
                        // Clear error when user types
                        if (e.target.value && errors.city) {
                          const newErrors = { ...errors };
                          delete newErrors.city;
                          setErrors(newErrors);
                        }
                      }}
                      placeholder={i18n('location_placeholder_city')}
                    />
                    {errors.city && <ErrorText colors={colors}>{errors.city}</ErrorText>}
                  </InputGroup>
                </TwoColumnSection>

                <Section>
                  <Label text={i18n('interpretation_location_details')} required />
                  <Input
                    colors={colors}
                    type="text"
                    value={formData.arrivalInstruction || ''}
                    onChange={(e) => updateField('arrivalInstruction', e.target.value)}
                    placeholder={i18n('interpretation_location_details')}
                  />
                  {errors.arrivalInstruction && <ErrorText colors={colors}>{errors.arrivalInstruction}</ErrorText>}
                </Section>
              </>
            )}

          </FormContent>
        </TwoColumnLayout>
      </CardContainer>
    </StepContainer>
  );
});

PrebookingDetailsStep.displayName = 'PrebookingDetailsStep';

PrebookingDetailsStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  addTimeSlot: PropTypes.func.isRequired,
  removeTimeSlot: PropTypes.func.isRequired,
  updateTimeSlot: PropTypes.func.isRequired,
  wizardMode: PropTypes.oneOf(['create', 'view', 'edit']),
};

export default PrebookingDetailsStep;
